import axios from 'axios';

const LIVE_API_BASE = 'https://api.tippay.co.in/api/v1';

const api = axios.create({
  baseURL: `${LIVE_API_BASE}/web`,
  timeout: 15000
});

/** Unauthenticated calls — same OTP flow as Android (`/api/v1/auth/send-otp`, `verify-otp`). */
const v1Public = axios.create({
  baseURL: LIVE_API_BASE,
  timeout: 15000
});

v1Public.interceptors.response.use(
  (response) => response,
  (error) => {
    const d = error?.response?.data;
    const message =
      (typeof d?.message === 'string' && d.message) ||
      (typeof d?.error === 'string' && d.error) ||
      error.message ||
      'Request failed';
    return Promise.reject(new Error(message));
  }
);

async function unwrapV1Success(promise) {
  const { data: body } = await promise;
  // Auth errors may use HTTP 203 (legacy) — axios still resolves; always check success flag.
  if (!body || body.success === false) {
    throw new Error(body?.message || body?.error || 'Request failed');
  }
  return body.data;
}

const IDEMPOTENT_POST_PREFIXES = ['/payout/distribute', '/payout/individual'];

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (
    config.method === 'post' &&
    IDEMPOTENT_POST_PREFIXES.some((p) => String(config.url || '').startsWith(p))
  ) {
    if (!config.headers['Idempotency-Key']) {
      config.headers['Idempotency-Key'] = crypto.randomUUID();
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    const message = error?.response?.data?.message || error.message || 'Request failed';
    return Promise.reject(new Error(message));
  }
);

const unwrap = async (promise) => {
  const response = await promise;
  return response.data?.data;
};

export const authPublicApi = {
  sendLoginOtp: (phone) =>
    unwrapV1Success(v1Public.post('/auth/send-otp', { phone, context: 'login' })),
  verifyLoginOtp: (phone, otp) =>
    unwrapV1Success(v1Public.post('/auth/verify-otp', { phone, otp, context: 'login' }))
};

export const webApi = {
  login: async ({ identifier, password }) => {
    const response = await api.post('/auth/login', { identifier, password });
    return response.data?.data;
  },
  changePassword: (payload) => unwrap(api.post('/auth/change-password', payload)),

  getSummary: (period = 'month', axiosConfig = {}) =>
    unwrap(api.get('/dashboard/summary', { params: { period }, ...axiosConfig })),
  getTopEarners: (period = 'month', limit = 5, axiosConfig = {}) =>
    unwrap(api.get('/dashboard/top-earners', { params: { period, limit }, ...axiosConfig })),
  getEmployerDashboard: (period = 'month', axiosConfig = {}) =>
    unwrap(api.get('/dashboard/full', { params: { period }, ...axiosConfig })),
  getDashboardQr: (axiosConfig = {}) => unwrap(api.get('/dashboard/qr', axiosConfig)),
  getTips: (params, axiosConfig = {}) => unwrap(api.get('/tips', { params, ...axiosConfig })),
  getOverallReviews: (axiosConfig = {}) => unwrap(api.get('/reviews/overall', axiosConfig)),
  getAllReviews: (period = 'today', axiosConfig = {}) => {
    const params =
      typeof period === 'object' && period !== null && !Array.isArray(period)
        ? period
        : { period };
    return unwrap(api.get('/reviews/all', { params, ...axiosConfig }));
  },

  getTransactionsOverview: (recentLimit = 5, axiosConfig = {}) =>
    unwrap(api.get('/transactions/overview', { params: { recentLimit }, ...axiosConfig })),
  getRecentTransactions: (limit = 10, axiosConfig = {}) =>
    unwrap(api.get('/transactions/recent', { params: { limit }, ...axiosConfig })),
  getTransactionReport: (params, axiosConfig = {}) =>
    unwrap(api.get('/transactions/report', { params, ...axiosConfig })),
  downloadTransactionReportCsv: async (period = 'today', axiosConfig = {}) => {
    const response = await api.get('/transactions/report.csv', {
      params: { period },
      responseType: 'blob',
      ...axiosConfig
    });
    return response.data;
  },
  linkEmployeeByPhone: (payload) => unwrap(api.post('/employees/link-by-phone', payload)),
  linkEmployeeByCode: (payload) => unwrap(api.post('/employees/link-by-code', payload)),
  getEmployees: (axiosConfig = {}) => unwrap(api.get('/employees/list', axiosConfig)),
  getEmployee: (employeeId, axiosConfig = {}) => unwrap(api.get(`/employees/${employeeId}`, axiosConfig)),
  searchEmployee: (payload) => unwrap(api.post('/employees/search', payload)),
  unlinkEmployee: (payload) => unwrap(api.delete('/employees/unlink', { data: payload, params: payload })),

  getPoolEmployees: (axiosConfig = {}) => unwrap(api.get('/payout/pool-employees', axiosConfig)),
  distributePool: (payload) => unwrap(api.post('/payout/distribute', payload)),
  getPayoutDetails: (tipType = 'individual', axiosConfig = {}) =>
    unwrap(api.get('/payout/details', { params: { tipType }, ...axiosConfig })),
  createIndividualPayout: (payload) => unwrap(api.post('/payout/individual', payload)),
  getPayoutHistory: (params, axiosConfig = {}) =>
    unwrap(api.get('/payout/history', { params, ...axiosConfig })),

  getShopInfo: (axiosConfig = {}) => unwrap(api.get('/profile/shop-info', axiosConfig)),
  updateShopInfo: (payload) => unwrap(api.put('/profile/shop-info', payload)),
  getProfileUploadUrl: (payload) => unwrap(api.post('/profile/upload-url', payload)),
  confirmProfilePictureUpload: (payload) => unwrap(api.post('/profile/confirm-upload', payload)),
  updateTipMode: (payload) => unwrap(api.put('/profile/tip-mode', payload)),
  updatePoolPaymentProvider: (payload) => unwrap(api.put('/profile/payment-provider', payload)),

  getBankAccounts: (axiosConfig = {}) => unwrap(api.get('/verification/bank-accounts', axiosConfig)),
  verifyBankAccount: (payload) => unwrap(api.post('/verification/bank-account/verify', payload)),
  linkBankAccount: (payload) => unwrap(api.post('/verification/bank-account/link', payload)),
  verifyGstin: (payload) => unwrap(api.post('/verification/gstin/verify', payload)),
  linkGstin: (payload) => unwrap(api.post('/verification/gstin/link', payload)),

  getNotifications: (limit = 20, offset = 0, axiosConfig = {}) =>
    unwrap(api.get('/notifications', { params: { limit, offset }, ...axiosConfig })),
  getUnreadCount: (axiosConfig = {}) => unwrap(api.get('/notifications/unread-count', axiosConfig)),
  markNotificationRead: (notificationId) => unwrap(api.put(`/notifications/${notificationId}/read`)),
  markAllNotificationsRead: () => unwrap(api.put('/notifications/mark-all-read'))
};

export default api;
